import React, { createContext } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "src/hooks/useAuth";
import useMessage from "src/hooks/useMessage";
import { DataContextType } from "src/types/dataContextTypes";
import api from "src/utils/apiConfig";

const DataContext = createContext<DataContextType>({} as DataContextType);

export const DataProvider = ({ children }: { children: React.ReactNode }) => {
    const navigate = useNavigate();

    const { forceLogoutDisabledUser, forceLogoutUser } = useAuth();
    const { showInfo } = useMessage();

    const handleAPIError = async (error: any) => {
        const errorCode = error?.response?.status;
        const errorMessage = error?.response?.data?.message || error.message;

        console.log(`${errorCode} Error with Message ${errorMessage} Received`);

        if (!errorMessage) {
            throw "Unhandled Server Error";
        }

        if (errorMessage === "Please complete your profile details") {
            showInfo("Please complete your profile details");
            navigate("/profileSettings");
        }

        if (errorMessage === "Your account is disabled") {
            forceLogoutDisabledUser();
            throw new Error("Your account is disabled");
        }

        if (
            errorMessage === "Expired session" ||
            errorMessage === "Refresh token expired"
        ) {
            forceLogoutUser();
            throw new Error("Your session has expired! Please log in again.");
        }

        if (errorMessage === "Network Error") {
            forceLogoutUser();
            throw new Error(
                "Server is unavailable. Please login again in a few minutes.",
            );
        }

        if (errorMessage === "Network Connection Issue.") {
            throw new Error(
                "Network Issue. Please check your network connection.",
            );
        }

        if (errorCode === 429) {
            showInfo("Too many requests. Please try again later");
        }

        if (errorMessage[0] === "domainName must be a valid domain name") {
            throw new Error("Please enter a valid domain name");
        }

        throw new Error("Server unavailable. Please try again later.");
    };

    const fetchData = async (url: string) => {
        try {
            if (!navigator.onLine) {
                throw new Error("Network Connection Issue.");
            }
            const response = await api.get(url, {});
            return response.data;
        } catch (error: any) {
            await handleAPIError(error);
        }
    };

    const postData = async (url: string, data: any) => {
        try {
            if (!navigator.onLine) {
                throw new Error("Network Connection Issue.");
            }
            const response = await api.post(url, data);
            return response.data;
        } catch (error: any) {
            await handleAPIError(error);
        }
    };

    const patchData = async (url: string, data: any) => {
        try {
            if (!navigator.onLine) {
                throw new Error("Network Connection Issue.");
            }
            const response = await api.patch(url, data, {});
            return response.data;
        } catch (error: any) {
            await handleAPIError(error);
        }
    };

    const deleteData = async (url: string) => {
        try {
            if (!navigator.onLine) {
                throw new Error("Network Connection Issue.");
            }
            const response = await api.delete(url, {});
            return response.data;
        } catch (error: any) {
            await handleAPIError(error);
        }
    };

    return (
        <DataContext.Provider
            value={{ fetchData, postData, patchData, deleteData }}
        >
            {children}
        </DataContext.Provider>
    );
};

export default DataContext;
